import React, { useEffect, useState } from "react";
import { Search, ChevronLeft, ChevronRight, Loader } from "lucide-react";

// Modern Table Component with Bootstrap
function Table({
  columns,
  data,
  keyField = "id",
  searchValue = "",
  isLoading = false,
}) {
  const [searchTerm, setSearchTerm] = useState(searchValue);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(15);
  const [inputPage, setInputPage] = useState("");

  // Filtered data based on search term
  const filteredData = (data || []).filter((row) =>
    columns.some((column) =>
      String(row[column.accessor] || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  );

  useEffect(() => {
    setSearchTerm(searchValue);
  }, [searchValue]);

  // Pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setInputPage("");
    }
  };

  const handlePageInputChange = (e) => {
    setInputPage(e.target.value);
  };

  const handlePageInputSubmit = (e) => {
    if (e.key === "Enter") {
      const pageNumber = Number(inputPage);
      if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
        handlePageChange(pageNumber);
      } else {
        setInputPage("");
      }
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  return (
    <div
      className="container-fluid py-4"
      style={{
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        minHeight: "100vh",
      }}
    >
      <div className="container-fluid">
        <div
          className="card border-0 shadow-lg"
          style={{ borderRadius: "16px", overflow: "hidden" }}
        >
          {/* Header */}
          <div
            className="card-header bg-white border-0"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            <div className="row align-items-center py-3">
              <div className="col-md-6 mb-3 mb-md-0">
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 me-3"
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "rgba(255,255,255,0.2)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <i className="bi bi-table text-white fs-4"></i>
                  </div>
                  <div>
                    <h4 className="mb-1 fw-bold text-white">Data Table</h4>
                    <p className="mb-0 text-white-50 small">
                      <span className="badge bg-white text-primary fw-semibold">
                        {filteredData.length}
                      </span>
                      <span className="ms-2">
                        {filteredData.length === 1 ? "record" : "records"} found
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="input-group shadow-sm">
                  <span className="input-group-text bg-white border-0">
                    <Search size={18} className="text-muted" />
                  </span>
                  <input
                    type="text"
                    className="form-control border-0"
                    placeholder="Search records..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="card-body p-0">
            {isLoading ? (
              <div className="text-center py-5">
                <Loader
                  className="text-primary mb-3"
                  size={48}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                <h5 className="text-muted">Loading data...</h5>
                <p className="text-muted small">
                  Please wait while we fetch your records
                </p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table
                    className="table table-hover mb-0"
                    style={{ minWidth: "800px" }}
                  >
                    <thead
                      style={{
                        background:
                          "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                      }}
                    >
                      <tr>
                        {columns?.map((column, index) => (
                          <th
                            key={index}
                            className="text-nowrap px-4 py-3 fw-semibold text-secondary border-0"
                            style={{
                              fontSize: "0.875rem",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {column.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentRows?.length > 0 ? (
                        currentRows?.map((row, rowIndex) => (
                          <tr
                            key={row[keyField]}
                            style={{
                              transition: "all 0.2s ease",
                              background:
                                rowIndex % 2 === 0 ? "#ffffff" : "#f8f9fa",
                            }}
                          >
                            {columns?.map((column, index) => (
                              <td
                                key={index}
                                className="text-nowrap px-4 py-3 border-0"
                                style={{ fontSize: "0.9rem" }}
                              >
                                {column.Cell
                                  ? column.Cell({ value: row[column.accessor] })
                                  : row[column.accessor]}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={columns?.length}
                            className="text-center py-5 border-0"
                          >
                            <div className="text-muted">
                              <i className="bi bi-inbox fs-1 d-block mb-3"></i>
                              <h5>No Data Available</h5>
                              <p className="small mb-0">
                                Try adjusting your search criteria
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {filteredData.length > 0 && (
                  <div className="card-footer bg-white border-0 py-3">
                    <div className="row align-items-center">
                      <div className="col-md-4 mb-3 mb-md-0">
                        <p className="mb-0 text-muted small">
                          Showing{" "}
                          <span className="fw-semibold text-dark">
                            {indexOfFirstRow + 1}
                          </span>{" "}
                          to{" "}
                          <span className="fw-semibold text-dark">
                            {Math.min(indexOfLastRow, filteredData.length)}
                          </span>{" "}
                          of{" "}
                          <span className="fw-semibold text-dark">
                            {filteredData.length}
                          </span>{" "}
                          entries
                        </p>
                      </div>

                      <div className="col-md-4 mb-3 mb-md-0 text-center">
                        <nav>
                          <ul className="pagination pagination-sm justify-content-center mb-0">
                            <li
                              className={`page-item ${
                                currentPage === 1 ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link border-0 rounded-3 me-1"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                style={{
                                  background:
                                    currentPage === 1
                                      ? "#f8f9fa"
                                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                  color:
                                    currentPage === 1 ? "#6c757d" : "white",
                                }}
                              >
                                <ChevronLeft size={16} />
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "..." ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link border-0 bg-transparent">
                                    ...
                                  </span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link border-0 rounded-3 mx-1"
                                    onClick={() => handlePageChange(page)}
                                    style={{
                                      background:
                                        currentPage === page
                                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                          : "transparent",
                                      color:
                                        currentPage === page
                                          ? "white"
                                          : "#667eea",
                                      fontWeight:
                                        currentPage === page ? "600" : "400",
                                      minWidth: "36px",
                                    }}
                                  >
                                    {page}
                                  </button>
                                </li>
                              )
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link border-0 rounded-3 ms-1"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                style={{
                                  background:
                                    currentPage === totalPages
                                      ? "#f8f9fa"
                                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                  color:
                                    currentPage === totalPages
                                      ? "#6c757d"
                                      : "white",
                                }}
                              >
                                <ChevronRight size={16} />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>

                      <div className="col-md-4">
                        <div className="d-flex align-items-center justify-content-md-end">
                          <label className="text-muted small me-2 mb-0">
                            Go to:
                          </label>
                          <input
                            type="number"
                            className="form-control form-control-sm border-0 shadow-sm text-center"
                            style={{ width: "70px", background: "#f8f9fa" }}
                            value={inputPage}
                            onChange={handlePageInputChange}
                            onKeyDown={handlePageInputSubmit}
                            placeholder={currentPage.toString()}
                            min="1"
                            max={totalPages}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .table-hover tbody tr:hover {
          background: linear-gradient(90deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%) !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        
        .page-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }
        
        .card {
          transition: all 0.3s ease;
        }
        
        input:focus {
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25) !important;
          border-color: #667eea !important;
        }
      `}</style>
    </div>
  );
}

export default Table;
